import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError, API } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Upload, ArrowLeft, Wallet, Info } from "lucide-react";

export default function Topup() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [amount, setAmount] = useState(50);
  const [name, setName] = useState(user?.name || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (user && !name) setName(user.name || "");
  }, [user, name]);

  if (loading) return null;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Fail terlalu besar (max 8MB)");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Sila upload resit");
    if (amount <= 0) return toast.error("Amount tidak sah");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("amount", String(amount));
    fd.append("name", name.trim());
    fd.append("receipt", file);
    try {
      await api.post("/topups", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Permohonan top up dihantar. Menunggu admin approve.");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal hantar top up");
    } finally {
      setSubmitting(false);
    }
  };

  const qrSrc = settings?.tng_qr_path ? `${API}/files/${settings.tng_qr_path}` : null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="text-xs text-zinc-500 hover:text-violet-400 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Dashboard
        </Link>
        <div className="mb-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-violet-400 mb-2">Top Up Wallet</div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Isi Baki via TNG</h1>
          <p className="text-sm text-zinc-400 mt-1">Baki semasa: <span className="text-white font-mono">RM {Number(user.balance).toFixed(2)}</span></p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {/* QR Card */}
          <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-5">
            <div className="text-[10px] uppercase tracking-widest text-violet-300 mb-3">Step 1 — Scan QR TNG</div>
            <div className="aspect-square rounded-xl bg-black/40 border border-white/5 grid place-items-center overflow-hidden">
              {qrSrc ? (
                <img data-testid="tng-qr-image" src={qrSrc} alt="TNG QR" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center p-6">
                  <Info className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">QR belum di-upload oleh admin.</p>
                  <p className="text-xs text-zinc-600 mt-1">Sila hubungi WhatsApp untuk bantuan manual.</p>
                </div>
              )}
            </div>
            <div className="mt-3 text-xs text-zinc-500 leading-relaxed">
              Scan QR di atas menggunakan aplikasi Touch 'n Go eWallet. Pastikan amount yang anda bayar sama dengan amount yang anda isi di bawah.
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} data-testid="topup-form" className="rounded-2xl bg-[#0f0f12] border border-white/5 p-5 flex flex-col">
            <div className="text-[10px] uppercase tracking-widest text-violet-300 mb-3">Step 2 — Isi Butiran</div>

            <label className="block mb-4">
              <span className="text-xs text-zinc-400 mb-1 block">Amount (RM)</span>
              <Input data-testid="topup-amount-input" type="number" min="1" step="0.01" required value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="h-12 rounded-xl bg-[#121214] border-white/10 font-mono focus-visible:ring-violet-500 focus-visible:border-violet-500" />
              <div className="mt-2 flex flex-wrap gap-2">
                {[10, 50, 100, 300, 500].map((v) => (
                  <button type="button" key={v} onClick={() => setAmount(v)} data-testid={`preset-${v}`} className="text-xs px-2.5 py-1 rounded-lg border border-white/10 bg-white/[0.03] hover:border-violet-500/40">RM {v}</button>
                ))}
              </div>
            </label>

            <label className="block mb-4">
              <span className="text-xs text-zinc-400 mb-1 block">Nama Pembayar</span>
              <Input data-testid="topup-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl bg-[#121214] border-white/10 focus-visible:ring-violet-500 focus-visible:border-violet-500" placeholder="Nama seperti dalam TNG" />
            </label>

            <label className="block mb-4">
              <span className="text-xs text-zinc-400 mb-1 block">Upload Resit (JPG / PNG / PDF, max 8MB)</span>
              <div className="rounded-xl border-2 border-dashed border-white/10 hover:border-violet-500/40 transition-colors p-4 text-center cursor-pointer">
                <input data-testid="topup-receipt-input" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" id="receipt-file" />
                <label htmlFor="receipt-file" className="cursor-pointer block">
                  {preview ? (
                    <img src={preview} alt="preview" className="max-h-40 mx-auto rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                      <div className="text-sm text-zinc-400">Klik untuk pilih fail resit</div>
                    </>
                  )}
                </label>
                {file && <div className="text-[11px] text-zinc-500 mt-2 truncate">{file.name}</div>}
              </div>
            </label>

            <button type="submit" disabled={submitting} data-testid="topup-submit" className="mt-auto h-12 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-60 font-semibold flex items-center justify-center gap-2 purple-glow">
              <Wallet className="w-4 h-4" />
              {submitting ? "Menghantar..." : "Hantar untuk Approve"}
            </button>
            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              Admin akan approve resit anda dalam masa 5-30 minit. Baki akan ditambah automatik selepas approve.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
